// pages/secure/MySiteSearchesPage.js — My Site Searches (/dashboard/my-site-searches).
//
// Owner, 22 Oct 2026: "make one portal for me like Renata's for these as managing broker to
// perform these searches … every day make a sweep of searches in the towns for BY RIGHT for
// those uses and present the results by town and addresses to me directly."
//
// The managing broker's own searches — not filed under a client — for one or more uses at
// once (vocational school, cosmetology school, colleges & universities, or any industry
// typed in). The daily sweep looks only in districts that are by right for at least one use
// in each town (the Zoning Library), and this page opens on those by-right finds, grouped by
// town and then address. The Library coverage panel shows, town by town and use by use,
// what the library says and where research is missing.
//
// Server: routes/brokerSiteSearch.js (/api/broker-site-searches); candidate status / notes
// go through the ordinary /api/site-searches/:id/candidates/:cid route.
//
// Residential searches (owner, 26 Oct 2026 — homes by bed/bath, sale and rent, grouped by city,
// 3 times a day, email new finds): ResidentialSearch.js, chosen with the type picker in New search.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';
import { BusinessMultiPicker, NotResearched, ResearchNowButton } from '../../components/secure/BusinessPicker';
import LookupLinks, { AddressLink, LinkNote } from '../../components/shared/LookupLinks';
import { ResidentialForm, ResidentialDetail, EmailSiteSwitch, FrequencyOptions, FREQUENCY_LABEL } from './ResidentialSearch';

const API = '/api/broker-site-searches';

const USE_TONE = {
    by_right: 'bg-green-100 text-green-800 border-green-300',
    special_use: 'bg-amber-100 text-amber-800 border-amber-300',
    not_allowed: 'bg-red-100 text-red-800 border-red-300',
    unverified: 'bg-gray-100 text-gray-600 border-gray-300',
};
const USE_LABEL = { by_right: 'BY RIGHT', special_use: 'SPECIAL USE', not_allowed: 'NO', unverified: 'UNVERIFIED' };
const STATUS_ORDER = ['by_right', 'special_use', 'unverified', 'not_allowed'];
const CAND_STATUSES = ['new', 'shortlisted', 'toured', 'offer', 'rejected', 'off_market', 'closed'];
const PARKED = ['rejected', 'off_market', 'closed'];
const STATUS_LABEL = { off_market: 'off market' };
const STATUS_TONE = {
    new: 'bg-blue-50 text-blue-700', shortlisted: 'bg-green-50 text-green-700', toured: 'bg-indigo-50 text-indigo-700',
    offer: 'bg-purple-50 text-purple-700', rejected: 'bg-gray-100 text-gray-500', closed: 'bg-gray-100 text-gray-500',
    off_market: 'bg-stone-200 text-stone-600',
};
const VIEWS = [
    ['by_right', 'By right'],
    ['possible', 'By right or special use'],
    ['all', 'Everything found'],
];
// Short names for the badges, so three uses fit on one line.
const shortUse = (u) => {
    const s = String(u || '');
    if (/^vocational school$/i.test(s)) return 'Vocational';
    if (/^cosmetology/i.test(s)) return 'Cosmetology';
    if (/^college/i.test(s)) return 'College';
    return s.length > 18 ? `${s.slice(0, 17)}…` : s;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const lc = (s) => String(s || '').trim().toLowerCase();

const Badge = ({ status, children, title }) => (
    <span title={title} className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-semibold ${USE_TONE[status] || USE_TONE.unverified}`}>
        {children}
    </span>
);

const UseBadges = ({ c }) => (
    <span className="flex flex-wrap gap-1">
        {(c.uses || []).map((u) => (
            <Badge key={u.use_category} status={u.use_status} title={`${u.use_category}: ${USE_LABEL[u.use_status]}${u.reason ? ` — ${u.reason}` : ''}`}>
                {shortUse(u.use_category)}: {USE_LABEL[u.use_status]}
            </Badge>
        ))}
    </span>
);

// The same filter the server's CSV export applies (lib/siteSearchUses.filterCandidates).
const applyFilters = (rows, f) => rows.filter((c) => {
    if (f.view === 'by_right' && c.best_status !== 'by_right') return false;
    if (f.view === 'possible' && !['by_right', 'special_use'].includes(c.best_status)) return false;
    if (f.town && lc(c.municipality) !== lc(f.town)) return false;
    if (f.status && c.status !== f.status) return false;
    if (!f.status && !f.showParked && PARKED.includes(c.status)) return false;
    if (f.newOnly && !c.is_new) return false;
    if (f.q) {
        const hay = [c.address, c.municipality, c.zoning_district, c.listing_agent, c.listing_brokerage, c.notes, c.pin].join(' ').toLowerCase();
        if (!hay.includes(lc(f.q))) return false;
    }
    return true;
});

const groupByTown = (rows) => {
    const m = new Map();
    for (const c of rows) {
        const t = (c.municipality || '').trim() || 'Town not known';
        if (!m.has(t)) m.set(t, []);
        m.get(t).push(c);
    }
    return [...m.entries()]
        .sort(([a], [b]) => (a === 'Town not known') - (b === 'Town not known') || a.localeCompare(b))
        .map(([town, list]) => ({ town, list: list.sort((a, b) => String(a.address).localeCompare(String(b.address), 'en', { numeric: true })) }));
};

async function downloadExport(url, name) {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
    if (!r.ok) {
        let m = `Could not export (${r.status}).`;
        try { m = (await r.json()).message || m; } catch (e) { /* not JSON */ }
        throw new Error(m);
    }
    const href = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = href; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
}

// ---------------------------------------------------------------------------
// Create / edit
// ---------------------------------------------------------------------------

const ChipPicker = ({ label, options, value, onChange, placeholder, hint }) => {
    const [typed, setTyped] = useState('');
    const has = (x) => value.some((v) => lc(v) === lc(x));
    const toggle = (x) => onChange(has(x) ? value.filter((v) => lc(v) !== lc(x)) : [...value, x]);
    const addTyped = () => {
        const parts = typed.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
        const next = [...value];
        for (const p of parts) if (!next.some((v) => lc(v) === lc(p))) next.push(p);
        onChange(next); setTyped('');
    };
    const extra = value.filter((v) => !options.some((o) => lc(o) === lc(v)));
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</p>
            {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
            <div className="mt-1 flex max-h-40 flex-wrap gap-1 overflow-y-auto rounded border border-gray-200 p-2">
                {options.map((o) => (
                    <button type="button" key={o} onClick={() => toggle(o)}
                            className={`rounded-full border px-2 py-0.5 text-xs ${has(o) ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {o}
                    </button>
                ))}
                {extra.map((o) => (
                    <button type="button" key={`x-${o}`} onClick={() => toggle(o)} title="Not in the Zoning Library yet — click to remove"
                            className="rounded-full border border-indigo-700 bg-indigo-700 px-2 py-0.5 text-xs text-white">
                        {o} ×
                    </button>
                ))}
                {!options.length && !extra.length && <span className="text-xs text-gray-400">Nothing in the library yet — type below.</span>}
            </div>
            <div className="mt-1 flex gap-2">
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={placeholder}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }}
                       className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm" />
                <button type="button" onClick={addTyped} className="rounded border border-gray-300 px-3 py-1 text-xs">Add</button>
            </div>
        </div>
    );
};

// New search: Commercial (space by use and zoning) or Residential (homes). Editing keeps the type.
const SearchForm = (props) => {
    const { initial } = props;
    const [type, setType] = useState(initial && initial.criteria && initial.criteria.search_type === 'residential' ? 'residential' : 'commercial');
    return (
        <div className="space-y-4">
            {!initial && (
                <div className="flex flex-wrap gap-2 text-sm">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Type:</span>
                    {[['commercial', 'Commercial — space for a use (zoning, by right)'], ['residential', 'Residential — homes for sale / rent']].map(([k, l]) => (
                        <label key={k} className={`flex items-center gap-1 rounded border px-2 py-1 ${type === k ? 'border-slate-800 bg-slate-50' : 'border-gray-300'}`}>
                            <input type="radio" name="search_type" checked={type === k} onChange={() => setType(k)} /> {l}
                        </label>
                    ))}
                </div>
            )}
            {type === 'residential' ? <ResidentialForm {...props} /> : <CommercialForm {...props} />}
        </div>
    );
};

const CommercialForm = ({ initial, options, onSaved, onCancel }) => {
    const apiClient = useApiClient();
    const crit = (initial && initial.criteria) || {};
    const [f, setF] = useState({
        title: initial?.title || '',
        uses: initial?.uses || [],
        towns: crit.towns || [],
        min_sf: crit.min_sf || '',
        max_sf: crit.max_sf || '',
        notes: crit.notes || '',
        sweep_enabled: initial ? !!Number(initial.sweep_enabled) : true,
        sweep_frequency: initial?.sweep_frequency || 'daily',
        by_right_only: initial ? !!Number(initial.by_right_only) : true,
        email_new_finds: !!(crit.email && crit.email.enabled),
        email_recipients: ((crit.email && crit.email.recipients) || []).join(', '),
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const townsForUses = useMemo(() => {
        const combined = (options && options.combined) || {};
        const by = (options && options.towns_by_use) || {};
        const out = new Set();
        for (const u of f.uses) {
            for (const t of by[u] || []) out.add(t);
            const c = combined[lc(u)];
            if (c) for (const t of by[c] || []) out.add(t);
        }
        return [...out].sort();
    }, [options, f.uses]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setError('');
        try {
            const body = { ...f };
            if (initial) await apiClient.put(`${API}/${initial.search_id}`, body);
            else {
                const r = await apiClient.post(API, body);
                body.search_id = r.search_id;
            }
            onSaved(initial ? initial.search_id : body.search_id);
        } catch (err) {
            setError(err.message || 'Could not save.');
        } finally { setSaving(false); }
    };
    const field = 'mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm';
    const label = 'block text-xs font-semibold uppercase tracking-wide text-gray-600';
    return (
        <form onSubmit={submit} className="space-y-4">
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div>
                <label className={label}>Title</label>
                <input required className={field} value={f.title} onChange={set('title')} placeholder="Schools — vocational, cosmetology, colleges (by right)" />
            </div>
            <BusinessMultiPicker label="Uses (businesses)" value={f.uses} onChange={(uses) => setF({ ...f, uses })}
                hint="Any business in the Business Library — type any word (daycare, salon, warehouse…). The first one is the search's main use. A business not researched yet in a town shows as a gap you can send for research." />
            <ChipPicker label="Towns" options={(options && options.towns) || []} value={f.towns} onChange={(towns) => setF({ ...f, towns })}
                placeholder="A town not in the library yet, e.g. Glenview"
                hint="Towns the library has researched are listed; a town typed here shows as a gap in Library coverage until it is researched." />
            {!!townsForUses.length && (
                <button type="button" className="text-xs text-indigo-700 hover:underline"
                        onClick={() => setF({ ...f, towns: [...new Set([...f.towns, ...townsForUses])] })}>
                    Add every town the library has researched for these uses ({townsForUses.length})
                </button>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label className={label}>Smallest space (SF, optional)</label><input className={field} value={f.min_sf} onChange={set('min_sf')} inputMode="numeric" /></div>
                <div><label className={label}>Largest space (SF, optional)</label><input className={field} value={f.max_sf} onChange={set('max_sf')} inputMode="numeric" /></div>
            </div>
            <div>
                <label className={label}>Notes</label>
                <textarea className={field} rows={2} value={f.notes} onChange={set('notes')} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.sweep_enabled} onChange={set('sweep_enabled')} /> Automatic sweep on</label>
                <select className="rounded border border-gray-300 px-2 py-1 text-sm" value={f.sweep_frequency} onChange={set('sweep_frequency')}>
                    <FrequencyOptions />
                </select>
                <label className="flex items-center gap-2 text-sm" title="The sweep only looks in districts that are by right for at least one use">
                    <input type="checkbox" checked={f.by_right_only} onChange={set('by_right_only')} /> By-right districts only
                </label>
            </div>
            <div className="rounded border border-gray-200 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.email_new_finds} onChange={set('email_new_finds')} /> Email new finds after each sweep</label>
                <p className="text-[11px] text-gray-500">One email per run with only the addresses not emailed before. Nothing new = no email. Also needs the site's email switch (top of My Site Searches).</p>
                <input className={field} value={f.email_recipients} onChange={set('email_recipients')} placeholder="Send to: you@example.com, someone@example.com" />
            </div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                <button disabled={saving} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {saving ? 'Saving…' : initial ? 'Save' : 'Start search'}
                </button>
            </div>
        </form>
    );
};

// ---------------------------------------------------------------------------
// One result row's editable bits
// ---------------------------------------------------------------------------

const useCandidatePatch = (searchId, c, onChange) => {
    const apiClient = useApiClient();
    const [busy, setBusy] = useState(false);
    const patch = async (body) => {
        setBusy(true);
        try { await apiClient.put(`/api/site-searches/${searchId}/candidates/${c.candidate_id}`, body); onChange(); }
        catch (err) { alert(err.message || 'Could not save.'); }
        finally { setBusy(false); }
    };
    return { busy, patch };
};

const StatusSelect = ({ searchId, c, onChange, canEdit }) => {
    const { busy, patch } = useCandidatePatch(searchId, c, onChange);
    return (
        <select disabled={busy || !canEdit} value={c.status} onChange={(e) => patch({ status: e.target.value })}
                className={`rounded px-2 py-1 text-xs print:appearance-none ${STATUS_TONE[c.status] || ''}`}>
            {CAND_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
    );
};

const NotesCell = ({ searchId, c, onChange, canEdit }) => {
    const { busy, patch } = useCandidatePatch(searchId, c, onChange);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(c.notes || '');
    if (!editing) {
        return (
            <span className="text-xs text-gray-600">
                {c.notes || ''}
                {canEdit && <button type="button" onClick={() => { setText(c.notes || ''); setEditing(true); }}
                                    className="ml-1 text-[10px] text-indigo-700 hover:underline print:hidden">{c.notes ? 'edit' : 'add note'}</button>}
            </span>
        );
    }
    return (
        <span className="block">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} className="w-48 rounded border border-gray-300 p-1 text-xs" />
            <button type="button" disabled={busy} onClick={async () => { await patch({ notes: text }); setEditing(false); }}
                    className="mr-1 rounded bg-slate-800 px-2 py-0.5 text-[10px] text-white">Save</button>
            <button type="button" onClick={() => setEditing(false)} className="text-[10px] text-gray-600">Cancel</button>
        </span>
    );
};

// Owner, 25 Oct 2026: the address opens the listing (a building page is labelled as one), and
// "Look it up on:" searches LoopNet, Crexi, CityFeet and CommercialCafe for it (utils/listingLinks).
const AddressCell = ({ c, uses }) => (
    <>
        <p className="font-semibold text-gray-900">
            {c.is_new && <span className="mr-1 rounded bg-yellow-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-yellow-900">New</span>}
            <AddressLink c={c} className="text-blue-700 underline decoration-blue-300 hover:decoration-blue-700 print:text-gray-900 print:no-underline" />
        </p>
        {c.pin && <p className="text-[11px] text-gray-500">PIN {c.pin}</p>}
        <LinkNote c={c} className="text-[10px] text-gray-500 print:hidden" />
        <LookupLinks c={c} uses={uses} />
    </>
);

const AgentCell = ({ c }) => (
    <span className="text-xs">
        {c.listing_agent || '—'}
        {c.listing_brokerage && <span className="block text-gray-500">{c.listing_brokerage}</span>}
        {c.agent_phone && <a className="block font-mono text-[11px] text-blue-700 hover:underline" href={`tel:${c.agent_phone.replace(/[^\d+]/g, '')}`}>{c.agent_phone}</a>}
    </span>
);

// ---------------------------------------------------------------------------
// Results by town, then address
// ---------------------------------------------------------------------------

const TownGroups = ({ searchId, rows, onChange, canEdit, uses }) => {
    const groups = groupByTown(rows);
    if (!groups.length) return <p className="py-6 text-center text-sm text-gray-500">Nothing matches these filters.</p>;
    return (
        <div className="space-y-5">
            {groups.map((g) => (
                <section key={g.town} className="break-inside-avoid rounded border border-gray-200 bg-white">
                    <h3 className="flex flex-wrap items-baseline gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold text-gray-900">
                        {g.town}
                        <span className="text-xs font-normal text-gray-500">
                            {g.list.length} address{g.list.length === 1 ? '' : 'es'}
                            {' · '}{g.list.filter((c) => c.best_status === 'by_right').length} by right
                            {g.list.some((c) => c.is_new) && <> · <span className="font-semibold text-yellow-800">{g.list.filter((c) => c.is_new).length} new</span></>}
                        </span>
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
                                    <th className="px-3 py-1">Address</th><th className="px-2 py-1">District</th><th className="px-2 py-1">Zoning by use</th>
                                    <th className="px-2 py-1">Size</th><th className="px-2 py-1">Rate</th><th className="px-2 py-1">Listing agent</th>
                                    <th className="px-2 py-1">Status</th><th className="px-2 py-1">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.list.map((c) => (
                                    <tr key={c.candidate_id} className={`border-t border-gray-100 align-top ${PARKED.includes(c.status) ? 'bg-gray-50 text-gray-500' : ''}`}>
                                        <td className="px-3 py-2"><AddressCell c={c} uses={uses} /></td>
                                        <td className="px-2 py-2"><span className="whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px]">{c.zoning_district || '—'}</span>
                                            {!c.zoning_district && <span className="mt-1 block text-[10px] text-gray-500">district not known yet</span>}</td>
                                        <td className="px-2 py-2"><UseBadges c={c} /></td>
                                        <td className="px-2 py-2 font-mono text-[11px]">{c.size_sf || '—'}</td>
                                        <td className="px-2 py-2 font-mono text-[11px]">{c.rate || '—'}</td>
                                        <td className="px-2 py-2"><AgentCell c={c} /></td>
                                        <td className="px-2 py-2"><StatusSelect searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /></td>
                                        <td className="px-2 py-2"><NotesCell searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}
        </div>
    );
};

const flatColumns = (searchId, onChange, canEdit, uses) => [
    { key: 'municipality', label: 'Town', render: (c) => <span className="font-semibold">{c.municipality || '—'}</span> },
    { key: 'address', label: 'Address', value: (c) => `${c.address}${c.is_new ? ' NEW' : ''}`, render: (c) => <AddressCell c={c} uses={uses} /> },
    { key: 'zoning_district', label: 'District', render: (c) => <span className="font-mono text-[11px]">{c.zoning_district || '—'}</span> },
    { key: 'best_status', label: 'Zoning by use', value: (c) => (c.uses || []).map((u) => `${shortUse(u.use_category)}: ${USE_LABEL[u.use_status]}`).join('; '),
        sortValue: (c) => STATUS_ORDER.indexOf(c.best_status), type: 'number', render: (c) => <UseBadges c={c} /> },
    { key: 'size_sf', label: 'Size', type: 'number', value: (c) => c.size_sf || '', sortValue: (c) => c.size_sf_min ?? null },
    { key: 'rate', label: 'Rate' },
    { key: 'agent', label: 'Listing agent', value: (c) => [c.listing_agent, c.listing_brokerage, c.agent_phone].filter(Boolean).join(' · '), render: (c) => <AgentCell c={c} /> },
    { key: 'status', label: 'Status', type: 'number', value: (c) => STATUS_LABEL[c.status] || c.status, sortValue: (c) => CAND_STATUSES.indexOf(c.status),
        render: (c) => <StatusSelect searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /> },
    { key: 'first_seen_at', label: 'Found', type: 'date' },
    { key: 'notes', label: 'Notes', sortable: false, render: (c) => <NotesCell searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /> },
];

// ---------------------------------------------------------------------------
// Library coverage: town × use
// ---------------------------------------------------------------------------

const Coverage = ({ plan, uses, coverage, gaps, searchId, onQueued }) => (
    <div className="space-y-2">
        <NotResearched gaps={gaps} origin="my_searches" searchId={searchId} onDone={onQueued} />
        <p className="text-sm text-gray-700">
            {coverage.researched} of {coverage.cells} town-and-use answers are in the Zoning Library;
            {' '}<strong className={coverage.gaps ? 'text-red-700' : 'text-green-700'}>{coverage.gaps} missing</strong>.
            {' '}The sweep can look in {coverage.towns_searchable} of {coverage.towns} towns (those with at least one by-right district).
            {' '}<Link to="/dashboard/zoning-library" className="text-indigo-700 hover:underline print:hidden">Open the Zoning Library</Link>
        </p>
        <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 bg-white text-xs">
                <thead>
                    <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
                        <th className="px-2 py-1">Town</th>
                        {uses.map((u) => <th key={u} className="px-2 py-1">{u}</th>)}
                        <th className="px-2 py-1">Sweep looks in</th>
                    </tr>
                </thead>
                <tbody>
                    {plan.map((t) => (
                        <tr key={t.town} className="border-t border-gray-100 align-top">
                            <td className="px-2 py-2 font-semibold text-gray-900">{t.town}</td>
                            {t.uses.map((u) => (
                                <td key={u.use_category} className={`px-2 py-2 ${u.has_row ? '' : 'bg-red-50'}`}>
                                    {u.has_row ? (
                                        <>
                                            <Badge status={u.verdict_status}>{USE_LABEL[u.verdict_status] || u.verdict_status}</Badge>
                                            {u.districts_by_right && <span className="mt-1 block font-mono text-[11px] text-green-800">by right: {u.districts_by_right}</span>}
                                            {u.districts_special_use && <span className="block font-mono text-[11px] text-amber-800">hearing: {u.districts_special_use}</span>}
                                            {u.via && <span className="block text-[10px] text-gray-500">via {u.via}</span>}
                                            {!u.verified_on && <span className="block text-[10px] text-gray-500">not verified</span>}
                                        </>
                                    ) : (
                                        <span className="font-semibold text-red-700">Not researched
                                            {(gaps || []).some((g) => g.town === t.town && g.use_category === u.use_category && g.queued)
                                                ? <span className="block text-[10px] font-normal text-indigo-700">research queued</span>
                                                : <span className="mt-1 block"><ResearchNowButton business={u.use_category} towns={[t.town]} origin="my_searches" searchId={searchId} onDone={onQueued} /></span>}
                                        </span>
                                    )}
                                </td>
                            ))}
                            <td className="px-2 py-2 font-mono text-[11px]">
                                {t.by_right_districts.length ? t.by_right_districts.join(', ') : <span className="font-sans text-gray-500">nothing by right — skipped</span>}
                            </td>
                        </tr>
                    ))}
                    {!plan.length && <tr><td colSpan={uses.length + 2} className="px-2 py-4 text-center text-gray-500">No towns in this search yet. Edit it to add towns.</td></tr>}
                </tbody>
            </table>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// One search
// ---------------------------------------------------------------------------

const SearchDetail = ({ searchId, options, canEdit, onBack, onListChanged }) => {
    const apiClient = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const [tab, setTab] = useState('results');
    const [layout, setLayout] = useState('town');
    const [f, setF] = useState({ view: 'by_right', town: '', status: '', newOnly: false, showParked: false, q: '' });

    const load = useCallback(async () => {
        try { setData(await apiClient.get(`${API}/${searchId}`)); setError(''); }
        catch (err) { setError(err.message || 'Could not load the search.'); }
    }, [apiClient, searchId]);
    useEffect(() => { load(); }, [load]);

    const put = async (body) => {
        try { await apiClient.put(`${API}/${searchId}`, body); await load(); onListChanged(); }
        catch (err) { alert(err.message || 'Could not save.'); }
    };
    const runNow = async () => {
        try { const r = await apiClient.post(`${API}/${searchId}/run-now`, {}); alert(r.message); load(); }
        catch (err) { alert(err.message || 'Could not queue the search.'); }
    };
    const exportCsv = async () => {
        const qs = new URLSearchParams({ view: f.view, ...(f.town ? { town: f.town } : {}), ...(f.status ? { status: f.status } : {}),
            ...(f.newOnly ? { new: '1' } : {}), ...(f.showParked ? { parked: '1' } : {}) });
        try { await downloadExport(`${API}/${searchId}/export.csv?${qs}`, `site-search-${searchId}-${new Date().toISOString().slice(0, 10)}.csv`); }
        catch (err) { alert(err.message); }
    };

    if (error) return <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>;
    if (!data) return <p className="p-3 text-sm text-gray-500">Loading…</p>;
    if (data.residential) {
        return <ResidentialDetail data={data} searchId={searchId} canEdit={canEdit} onBack={onBack} onReload={load}
                                  onListChanged={onListChanged} kit={{ StatusSelect, NotesCell, downloadExport }} />;
    }
    const { search, uses, plan, coverage, candidates, counts, activity } = data;
    const crit = search.criteria || {};
    const shown = applyFilters(candidates, f);
    const towns = [...new Set(candidates.map((c) => c.municipality).filter(Boolean))].sort();
    const fresh = candidates.filter((c) => c.is_new && c.best_status === 'by_right');
    const setFil = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <button onClick={onBack} className="text-xs text-indigo-600 hover:underline print:hidden">← My searches</button>
                    <h2 className="text-xl font-bold text-gray-900">{search.title}</h2>
                    <div className="mt-1 flex flex-wrap gap-1">
                        {uses.map((u, i) => <span key={u} className={`rounded-full border px-2 py-0.5 text-xs ${i === 0 ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'}`}>{u}</span>)}
                    </div>
                    <p className="mt-1 text-xs text-gray-600">
                        {(crit.towns || []).length} towns{crit.min_sf || crit.max_sf ? ` · ${crit.min_sf || '—'}–${crit.max_sf || '—'} SF` : ''}
                        {' · '}{Number(search.by_right_only) ? 'by-right districts only' : 'all districts'}
                        {' · '}last run {fmtDate(search.last_swept_at)}
                    </p>
                    {search.last_sweep_summary && <p className="text-xs text-gray-500">Last run: {search.last_sweep_summary}</p>}
                    {search.sweep_requested_at && <p className="text-xs text-indigo-700">Run requested {fmtDate(search.sweep_requested_at)} — it starts at the office computer's next check (about every 30 minutes).</p>}
                    {crit.notes && <p className="text-xs text-gray-600">{crit.notes}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2 print:hidden">
                    <span className={`rounded px-2 py-1 text-xs font-semibold ${search.status === 'active' ? 'bg-green-100 text-green-800' : search.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>{search.status}</span>
                    {canEdit && search.status !== 'closed' && (
                        <>
                            <label className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs" title="The office computer runs the search by itself">
                                <input type="checkbox" checked={!!Number(search.sweep_enabled)} onChange={(e) => put({ sweep_enabled: e.target.checked })} />
                                Automatic sweep
                            </label>
                            <select value={search.sweep_frequency} onChange={(e) => put({ sweep_frequency: e.target.value })} className="rounded border border-gray-300 px-2 py-1 text-xs">
                                <FrequencyOptions />
                            </select>
                            <button onClick={runNow} disabled={!!search.sweep_requested_at || search.status !== 'active' || !Number(search.sweep_enabled)}
                                    className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                                {search.sweep_requested_at ? 'Run queued…' : 'Run search now'}
                            </button>
                            <button onClick={() => setEditing(true)} className="rounded border border-gray-300 px-3 py-1 text-xs">Edit</button>
                            {search.status === 'active'
                                ? <button onClick={() => put({ status: 'paused' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Pause</button>
                                : <button onClick={() => put({ status: 'active' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Resume</button>}
                            <button onClick={() => { if (window.confirm('Close this search? The sweep stops; the results stay.')) put({ status: 'closed' }); }}
                                    className="rounded border border-gray-300 px-3 py-1 text-xs">Close</button>
                        </>
                    )}
                    {canEdit && search.status === 'closed' && <button onClick={() => put({ status: 'active' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Reopen</button>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[['Addresses found', counts.total], ['By right', counts.by_right], ['Special use only', counts.special_use], ['New (3 days)', counts.fresh]].map(([k, n]) => (
                    <div key={k} className="rounded border border-gray-200 bg-white p-2">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500">{k}</p>
                        <p className="text-xl font-bold text-gray-900">{n}</p>
                    </div>
                ))}
            </div>

            {!!fresh.length && (
                <div className="rounded border-l-4 border-yellow-400 bg-yellow-50 p-3 text-sm">
                    <p className="font-semibold text-yellow-900">New by-right finds</p>
                    <ul className="mt-1 space-y-0.5 text-yellow-900">
                        {groupByTown(fresh).map((g) => (
                            <li key={g.town}><strong>{g.town}:</strong> {g.list.map((c) => `${c.address}${c.zoning_district ? ` (${c.zoning_district})` : ''}`).join('; ')}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex gap-1 border-b border-gray-200 print:hidden">
                {[['results', 'Results by town'], ['coverage', `Library coverage${coverage.gaps ? ` (${coverage.gaps} gaps)` : ''}`], ['activity', 'History']].map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)}
                            className={`rounded-t px-3 py-1.5 text-sm font-semibold ${tab === k ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{label}</button>
                ))}
            </div>

            {tab === 'results' && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs print:hidden">
                        <select value={f.view} onChange={setFil('view')} className="rounded border border-gray-300 px-2 py-1">
                            {VIEWS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <select value={f.town} onChange={setFil('town')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Every town</option>{towns.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={f.status} onChange={setFil('status')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Any status</option>{CAND_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                        </select>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={f.newOnly} onChange={setFil('newOnly')} /> New only</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={f.showParked} onChange={setFil('showParked')} /> Show rejected / off market</label>
                        {layout === 'town' && <input type="search" value={f.q} onChange={setFil('q')} placeholder="Search addresses, agents, notes…" className="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1" />}
                        <span className="text-gray-500">{shown.length} of {candidates.length}</span>
                        <button onClick={() => setLayout(layout === 'town' ? 'list' : 'town')} className="rounded border border-gray-300 px-2 py-1">
                            {layout === 'town' ? 'One sortable list' : 'Group by town'}
                        </button>
                        <button onClick={exportCsv} className="rounded border border-gray-300 bg-slate-100 px-2 py-1">CSV</button>
                        <button onClick={() => window.print()} className="rounded border border-gray-300 px-2 py-1">Print</button>
                    </div>
                    <p className="hidden text-xs text-gray-600 print:block">
                        {VIEWS.find(([k]) => k === f.view)[1]}{f.town ? ` · ${f.town}` : ''} · printed {new Date().toLocaleString()}
                    </p>
                    {layout === 'town'
                        ? <TownGroups searchId={searchId} rows={shown} onChange={load} canEdit={canEdit} uses={uses} />
                        : <DataList rows={shown} rowKey="candidate_id" csvName={`site-search-${searchId}`} columns={flatColumns(searchId, load, canEdit, uses)}
                                    defaultSort={{ key: 'municipality', dir: 'asc' }} empty="Nothing found yet."
                                    rowClass={(c) => (PARKED.includes(c.status) ? 'bg-gray-50 text-gray-500' : '')} />}
                    {!candidates.length && (
                        <p className="rounded bg-gray-50 p-3 text-sm text-gray-600">
                            Nothing found yet. The office computer runs this search {search.sweep_frequency === 'manual' ? 'when you press Run search now' : `${FREQUENCY_LABEL[search.sweep_frequency] || search.sweep_frequency}`}
                            {' '}in the by-right districts listed under Library coverage.
                        </p>
                    )}
                </div>
            )}

            {tab === 'coverage' && <Coverage plan={plan} uses={uses} coverage={coverage} gaps={data.not_researched || []} searchId={search.search_id} onQueued={load} />}

            {tab === 'activity' && (
                <ul className="space-y-1 text-xs text-gray-700">
                    {activity.map((a) => (
                        <li key={a.activity_id || `${a.created_at}-${a.event}`}><span className="text-gray-500">{fmtDate(a.created_at)}</span> · {a.event.replace(/_/g, ' ')}{a.detail ? ` — ${a.detail}` : ''}{a.actor ? ` (${a.actor})` : ''}</li>
                    ))}
                    {!activity.length && <li className="text-gray-500">No history yet.</li>}
                </ul>
            )}

            <Modal open={editing} onClose={() => setEditing(false)} title="Edit search" size="lg">
                {editing && <SearchForm initial={search} options={options} onCancel={() => setEditing(false)}
                                        onSaved={() => { setEditing(false); load(); onListChanged(); }} />}
            </Modal>
        </div>
    );
};

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

const MySiteSearchesPage = () => {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [list, setList] = useState(null);
    const [options, setOptions] = useState(null);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const openId = Number(params.get('id')) || null;
    const canEdit = !!user && user.staffRole !== 'Read Only';

    const loadList = useCallback(async () => {
        try { setList(await apiClient.get(API)); setError(''); }
        catch (err) { setError(err.message || 'Could not load your searches.'); }
    }, [apiClient]);
    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => { apiClient.get(`${API}/options`).then(setOptions).catch(() => setOptions({ uses: [], towns: [], towns_by_use: {} })); }, [apiClient]);
    const open = (id) => setParams(id ? { id: String(id) } : {});

    // One search and nothing chosen: open it straight away.
    useEffect(() => {
        if (!openId && list && list.length === 1 && !params.get('list')) setParams({ id: String(list[0].search_id) }, { replace: true });
    }, [openId, list, params, setParams]);

    if (error && !list) return <div className="p-6 text-sm text-red-700">{error}</div>;
    return (
        <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
            {!openId && (
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">My Site Searches</h1>
                        <p className="max-w-3xl text-sm text-slate-600">
                            The office's own searches for space, by use — not filed under a client. The office computer sweeps each
                            active search (daily by default) in the districts where at least one of its uses is allowed by right, and the
                            finds land here by town and address. A residential search looks for homes (for sale and for rent) by bedrooms
                            and bathrooms, and lists them by city.
                        </p>
                    </div>
                    {canEdit && <button onClick={() => setCreating(true)} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white">New search</button>}
                </div>
            )}
            {!openId && <EmailSiteSwitch canEdit={canEdit} />}

            {openId ? (
                <SearchDetail key={openId} searchId={openId} options={options} canEdit={canEdit}
                              onBack={() => setParams({ list: '1' })} onListChanged={loadList} />
            ) : !list ? <p className="text-sm text-gray-500">Loading…</p> : (
                <div className="grid gap-3 md:grid-cols-2">
                    {list.map((s) => (
                        <button key={s.search_id} onClick={() => open(s.search_id)}
                                className="rounded border border-gray-200 bg-white p-4 text-left shadow-sm hover:border-slate-400">
                            <p className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-gray-900">{s.title}</span>
                                <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${s.status === 'active' ? 'bg-green-100 text-green-800' : s.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>{s.status}</span>
                            </p>
                            <p className="mt-1 text-xs text-gray-600">
                                {(s.criteria || {}).search_type === 'residential'
                                    ? <><span className="mr-1 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-800">Residential</span>{((s.criteria || {}).pairs || []).map((p) => `${p.beds} bd / ${p.baths} ba`).join(' · ')}</>
                                    : (s.uses || []).join(' · ')}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                                {((s.criteria || {}).towns || []).length} towns · {s.candidate_count} found
                                {Number(s.new_count) > 0 && <span className="ml-1 rounded bg-yellow-300 px-1 text-[10px] font-bold text-yellow-900">{s.new_count} new</span>}
                                {' · '}{Number(s.sweep_enabled) ? `runs ${FREQUENCY_LABEL[s.sweep_frequency] || s.sweep_frequency}` : 'sweep off'}
                                {' · '}last run {s.last_swept_at ? new Date(s.last_swept_at).toLocaleDateString() : 'never'}
                            </p>
                        </button>
                    ))}
                    {!list.length && (
                        <p className="rounded bg-gray-50 p-4 text-sm text-gray-600">No searches yet.{canEdit ? ' Press New search to start one.' : ''}</p>
                    )}
                </div>
            )}

            <Modal open={creating} onClose={() => setCreating(false)} title="New search" size="lg">
                {creating && <SearchForm options={options} onCancel={() => setCreating(false)}
                                         onSaved={(id) => { setCreating(false); loadList(); open(id); }} />}
            </Modal>
        </div>
    );
};

export default MySiteSearchesPage;
